import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {ENV_TOKEN, EnvInterface} from 'env-config';
import {catchError, finalize, map, Observable, of, shareReplay, Subject, switchMap} from 'rxjs';
import {decodeJwtPayload} from '../utils/jwt';

export interface ValidatedUser {
  isValid: boolean;
  user?: any;
}

export const AUTH_TOKEN_KEY = 'authToken';
/** localStorage flag set by an explicit logout; cleared when the user signs in again. */
export const EXPLICIT_LOGOUT_KEY = 'explicitLogout';
/**
 * localStorage value replaced with a new id on every explicit logout and never cleared. Unlike the
 * flag above it changes even when the user was already logged out, so "has anyone logged out since
 * X?" can be answered by comparing ids, and other tabs see each logout as a storage event.
 */
export const LOGOUT_ID_KEY = 'explicitLogoutId';

@Injectable({providedIn: 'root'})
export class AuthValidationService {
  private http = inject(HttpClient);
  private environment = inject(ENV_TOKEN) as EnvInterface;
  private refreshInProgress$: Observable<ValidatedUser> | null = null;

  private readonly explicitLogoutSubject = new Subject<void>();
  /** Emits when the user explicitly logs out, on this tab or another tab of the site. */
  readonly explicitLogout$ = this.explicitLogoutSubject.asObservable();

  constructor() {
    // The storage event fires only in other tabs, so this relays their logouts to this one
    window.addEventListener('storage', event => {
      if (event.key === LOGOUT_ID_KEY && event.newValue) {
        this.explicitLogoutSubject.next();
      }
    });
  }

  /** Changes on every explicit logout in any tab; null if none has happened in this browser. */
  getLogoutId(): string | null {
    return localStorage.getItem(LOGOUT_ID_KEY);
  }

  // ── Access token (sessionStorage — cleared when tab closes) ──────────────

  getStoredToken(): string | null {
    return sessionStorage.getItem(AUTH_TOKEN_KEY);
  }

  storeToken(token: string): void {
    sessionStorage.setItem(AUTH_TOKEN_KEY, token);
    // Clear the explicit logout flag — user has actively logged in again
    localStorage.removeItem(EXPLICIT_LOGOUT_KEY);
  }

  clearToken(): void {
    sessionStorage.removeItem(AUTH_TOKEN_KEY);
  }

  // ── Logout ────────────────────────────────────────────────────────────────

  /**
   * Calls the server logout endpoint to revoke the access token server-side
   * and clear the HttpOnly refresh token cookie. Clears the access token locally.
   */
  revokeAndClearToken(): Observable<void> {
    const accessToken = this.getStoredToken();

    // Always clear locally first
    this.clearToken();

    // Persist the logout flag in localStorage so it survives page reloads and
    // navigation — prevents silent refresh from re-logging in the user.
    // Only cleared when the user actively logs in again (storeToken).
    localStorage.setItem(EXPLICIT_LOGOUT_KEY, 'true');

    // A new value on every logout, never cleared, separate from the flag above. The flag may already
    // be 'true', so writing it again neither changes anything comparable nor fires a storage event,
    // which browsers raise in other tabs only when a value actually changes. A fresh id makes every
    // logout detectable, both by an SSO handoff comparing the id at its start with the id now, and by
    // other tabs through the storage event (see the constructor).
    //
    // Timestamp plus random suffix: the timestamp keeps values unique over time and readable when
    // debugging; the suffix stops two logouts in the same millisecond (e.g. two tabs) writing the
    // same value, which would go unnoticed. It is a change marker, not a secret, so Math.random is
    // enough; crypto.randomUUID would also work but is only available on HTTPS or localhost.
    localStorage.setItem(LOGOUT_ID_KEY, `${Date.now()}-${Math.random().toString(36).slice(2)}`);

    // Lets an in-progress inbound SSO handoff stop rather than sign the user back in
    this.explicitLogoutSubject.next();

    const url = this.environment.security.tuatara.endpoint;
    const apiKey = this.environment.security.tuatara.apiKey;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
      ...(accessToken ? {'Authorization': `Bearer ${accessToken}`} : {}),
    });

    return this.http.post<void>(`${url}/auth/logout`, {}, {headers, withCredentials: true}).pipe(
      map(() => undefined),
      catchError(err => {
        console.warn('Server-side token revocation failed:', err?.status);
        return of(undefined);
      })
    );
  }

  // ── Decode ────────────────────────────────────────────────────────────────

  /**
   * Decodes the JWT payload for display purposes only (username, email, etc.).
   * NOT a security check — use validateToken() for access control.
   */
  getDecodedUser(): any | null {
    // base64URL-aware decode — atob() alone throws on tokens containing '-'/'_'.
    return decodeJwtPayload(this.getStoredToken());
  }

  // ── Validate (with silent refresh) ───────────────────────────────────────

  /**
   * Validates the access token server-side.
   * If the access token is missing or expired (401), silently calls /auth/refresh.
   * The browser automatically sends the HttpOnly refresh cookie — no JS token needed.
   * The user never sees a logout as long as their refresh cookie is valid.
   */
  validateToken(): Observable<ValidatedUser> {
    const token = this.getStoredToken();

    if (!token) {
      // No access token in sessionStorage (e.g. new tab) — try silent refresh
      return this.tryRefresh();
    }

    // Skip the HTTP call if the token is already expired — go straight to refresh
    if (this.isTokenExpired(token)) {
      return this.tryRefresh();
    }

    return this.callValidate(token).pipe(
      catchError(err => {
        if (err?.status === 401) {
          return this.tryRefresh();
        }
        console.warn('Token validation failed:', err?.status, err?.message);
        this.clearToken();
        return of({isValid: false});
      })
    );
  }

  /**
   * Silently refreshes the access token via the HttpOnly refresh cookie.
   * Resolves to the new access token, or null if the refresh failed (e.g. the
   * refresh cookie has expired or the user explicitly logged out).
   * Used by authRefreshInterceptor to recover from a 401 mid-session.
   */
  refreshAccessToken(): Observable<string | null> {
    return this.tryRefresh().pipe(
      map(result => (result.isValid ? this.getStoredToken() : null))
    );
  }

  private isTokenExpired(token: string): boolean {
    const payload = decodeJwtPayload(token);
    // Treat an unreadable/malformed token as expired so validateToken() falls back
    // to a silent refresh rather than trusting a token it cannot inspect.
    if (!payload) return true;
    if (!payload.exp) return false;
    // Consider expired if within 5 seconds of expiry
    return (payload.exp * 1000) <= (Date.now() + 5000);
  }

  // ── Private helpers ───────────────────────────────────────────────────────

  /**
   * Validates the current access token server-side without attempting a refresh.
   * Use this when you want to check token validity but must NOT trigger a silent
   * refresh (e.g. on the login page after logout).
   */
  callValidateOnly(): Observable<ValidatedUser> {
    const token = this.getStoredToken();
    if (!token) return of({isValid: false});
    return this.callValidate(token).pipe(
      catchError(() => of({isValid: false}))
    );
  }

  readAuthToken(): string {
    const token = sessionStorage.getItem('authToken');
    if (!token) {
      //console.error('No authToken in sessionStorage — user is not logged in');
      return '';
    }
    return token;
  }

  private callValidate(token: string): Observable<ValidatedUser> {
    const url = this.environment.security.tuatara.endpoint;
    const apiKey = this.environment.security.tuatara.apiKey;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
      'Authorization': `Bearer ${token}`,
    });

    return this.http.get<any>(`${url}/auth/validate-token`, {headers}).pipe(
      map(response => ({isValid: true, user: response}))
    );
  }

  /**
   * Calls POST /auth/refresh — no body needed.
   * The browser automatically sends the HttpOnly refresh cookie.
   * Quarkus reads it, validates it, and returns a new accessToken.
   *
   * shareReplay(1) ensures concurrent callers share one in-flight request,
   * preventing token rotation from being triggered multiple times simultaneously.
   */
  private tryRefresh(): Observable<ValidatedUser> {
    // Never silently refresh if the user explicitly logged out
    if (localStorage.getItem(EXPLICIT_LOGOUT_KEY) === 'true') {
      return of({isValid: false});
    }

    if (this.refreshInProgress$) return this.refreshInProgress$;

    const url = this.environment.security.tuatara.endpoint;
    const apiKey = this.environment.security.tuatara.apiKey;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
    });

    //console.log('tryRefresh: calling', `${url}/auth/refresh`, 'with withCredentials: true');

    this.refreshInProgress$ = this.http.post<{ accessToken: string }>(
      `${url}/auth/refresh`,
      {},
      {headers, withCredentials: true}
    ).pipe(
      switchMap(response => {
        //console.log('tryRefresh: response received, accessToken present:', !!response?.accessToken, response);
        this.storeToken(response.accessToken);
        //console.log('Access token silently refreshed via HttpOnly cookie');
        return this.callValidate(response.accessToken);
      }),
      catchError(err => {
        console.warn('tryRefresh FAILED — status:', err?.status, 'error:', err?.error, 'message:', err?.message);
        this.clearToken();
        return of({isValid: false});
      }),
      finalize(() => {
        this.refreshInProgress$ = null;
      }),
      shareReplay(1)
    );

    return this.refreshInProgress$;
  }
}
